// Composes BMD's two follow-up questions:
//   A  "Why did you pick that answer?", free text, Year 4 and above
//   B  "How sure are you?", a 1-5 scale, every year
//
// Surfaced after the student picks an answer, before the next MCQ. Both are
// optional and neither gates submit; that is unchanged, as is which years see
// the text box - the server still decides it and passes `showReason`.
//
// Side by side, per the mockup. They share the question's 60 seconds rather
// than having a clock of their own, so the layout has to be readable at a
// glance: short labels, and the confidence words always visible.

import React from "react"
import { Input } from "reactstrap"
import { BMD_COPY } from "../../constants/diagnostics/bmd.constants"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import { ConfidenceScale } from "./ConfidenceScale"

export type FollowUpQuestionsValue = {
  reason: string
  confident: number | null
}

export type FollowUpQuestionsProps = {
  value: FollowUpQuestionsValue
  onChange: (value: FollowUpQuestionsValue) => void
  readonly?: boolean
  reasonMaxLength?: number
  className?: string
  /**
   * Whether to ask WHY they chose their answer.
   *
   * Up to Year 3 the student gets the confidence rating only; the written
   * reason starts at Year 4. The confidence scale is shown to every year, so
   * only this half is gated.
   *
   * Defaults to true so any other caller keeps the previous behaviour.
   */
  showReason?: boolean
}

export const FollowUpQuestions: React.FC<FollowUpQuestionsProps> = ({
  value,
  onChange,
  readonly = false,
  reasonMaxLength = 225,
  className,
  showReason = true,
}) => {
  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, reason: event.target.value })
  }

  const handleConfidentChange = (confident: 1 | 2 | 3 | 4 | 5) => {
    onChange({ ...value, confident })
  }

  const heading: React.CSSProperties = {
    color: BMD_UI.TITLE,
    fontSize: 14,
    fontWeight: 700,
  }

  return (
    <div className={className}>
      <div className="row g-4">
        {showReason ? (
          <div className="col-12 col-lg-6">
            <label htmlFor="bmd-followup-reason" className="d-block mb-2">
              <span style={heading}>{BMD_COPY.FOLLOW_UP_WHY}</span>
              {/* Said on the label rather than in a tooltip: a child who thinks
                  this is required will sit and type instead of moving on, and
                  the clock is shared with the question itself. */}
              <span
                className="ms-2"
                style={{ color: BMD_UI.MUTED, fontSize: 12 }}
              >
                {BMD_COPY.FOLLOW_UP_OPTIONAL}
              </span>
            </label>
            <Input
              id="bmd-followup-reason"
              type="textarea"
              value={value.reason}
              onChange={handleReasonChange}
              disabled={readonly}
              maxLength={reasonMaxLength}
              rows={3}
              placeholder={BMD_COPY.FOLLOW_UP_WHY_PLACEHOLDER}
              style={{
                height: 96,
                border: `${BMD_UI.CONTROL_BORDER_WIDTH}px solid ${BMD_UI.CONTROL_BORDER}`,
                borderRadius: 8,
                fontSize: 14,
                resize: "none",
              }}
            />
          </div>
        ) : null}

        {/* Full width for the years that do not get the text box. */}
        <div className={showReason ? "col-12 col-lg-6" : "col-12"}>
          <div className="mb-2" style={heading}>
            {BMD_COPY.FOLLOW_UP_CONFIDENCE}
          </div>
          <ConfidenceScale
            value={value.confident}
            onChange={handleConfidentChange}
            readonly={readonly}
          />
        </div>
      </div>
    </div>
  )
}

export default FollowUpQuestions
